import { useCallback } from "react";

import { Column, Text, TagTextAlert } from "@bridgesplit/ui";
import { CheckCircle, Error as ErrorIcon } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import {
    TokenListMetadata,
    AsyncSwapStatus,
    usePandoraQuoteAndIxs,
    getPandoraErrorMessage,
    useTokenSwapTransaction
} from "@bridgesplit/abf-react";
import { explorerTxUrl, MISSING_PARAM_ERROR, Result } from "@bridgesplit/utils";
import { TransactionResult } from "@bridgesplit/react";
import { SwapInstructionsResponse, SwapParams } from "@loopscale/pandora-types";
import { AppButton, DialogHeaderWithBack } from "app/components/common";

import { useAsyncSwapSettlement } from "./useAsyncSwapSettlement";
import { useSignAndSendTransactionSender } from "../transactions/util";

interface AsyncSwapSettlementProps {
    redemptionId: string;
    routeType: string;
    buyToken?: TokenListMetadata;
    /** Two-step purchase: the swap delivers `buyToken` as an intermediate toward this final target. */
    finalBuyToken?: TokenListMetadata;
    /** Starts the second swap (`buyToken` → `finalBuyToken`) after settlement completes. */
    onContinue?: () => void;
    onClose?: () => void;
    inNestedDialogView?: boolean;
    /**
     * Present when the async flow requires the user to sign a second tx after settlement
     * (e.g. GLAM redeem → claim). Undefined for self-settling flows like Fission.
     */
    claimParams?: SwapParams;
    /** Route-specific poll param (e.g. GLAM vault state PDA). Forwarded to /async_swap/status. */
    pollState?: string;
}

/** Success UI variants, modeled as one discriminated state so render sites don't re-derive it. */
type SwapCompletion =
    | { kind: "final"; title: string }
    | { kind: "intermediate"; title: string; description: string; continueLabel: string; onContinue: () => void };

/**
 * Component responsible for displaying async swap settlement status.
 * Follows Single Responsibility Principle - only handles settlement UI.
 */
export function AsyncSwapSettlement({
    redemptionId,
    routeType,
    buyToken,
    finalBuyToken,
    onContinue,
    onClose,
    inNestedDialogView = false,
    claimParams,
    pollState
}: AsyncSwapSettlementProps) {
    const { status, settlementTxHash, error, isPolling, isComplete, isFailed } = useAsyncSwapSettlement({
        redemptionId,
        routeType,
        state: pollState
    });

    // Only request the claim quote once the redeem has settled; the backend derives the claim leg
    // from the transactionIdentifier we echo in optionalParams.
    const claimQuoteRequest = isComplete && claimParams ? claimParams : undefined;
    const {
        data: claimQuote,
        isLoading: claimQuoteLoading,
        isFetching: claimQuoteFetching,
        error: claimQuoteError
    } = usePandoraQuoteAndIxs(claimQuoteRequest);
    const claimQuoteIsLoading = claimQuoteLoading || claimQuoteFetching;

    const executeClaim = useExecuteClaim(claimQuote, claimParams?.userWallet, onClose);

    // A claim is pending when settlement finished but the user still owes a signature.
    const needsClaim = isComplete && !!claimParams;

    // The delivered token is an intermediate toward a different final target -> continuation flow.
    const completion: SwapCompletion =
        finalBuyToken && onContinue && finalBuyToken.mint !== buyToken?.mint
            ? {
                  kind: "intermediate",
                  title: "Step 1 of 2 Complete",
                  description: `Swap ${buyToken?.symbol} for ${finalBuyToken.symbol} to complete your purchase`,
                  continueLabel: `Swap ${buyToken?.symbol} for ${finalBuyToken.symbol}`,
                  onContinue
              }
            : { kind: "final", title: "Swap Complete" };

    return (
        <Column spacing={2}>
            {inNestedDialogView ? (
                <DialogHeaderWithBack back={onClose} header="Processing Swap" />
            ) : (
                <Text variant="h3">Processing Swap</Text>
            )}

            <Column spacing={3} sx={{ py: 4, alignItems: "center" }}>
                {isPolling && (
                    <>
                        <CircularProgress size={48} />
                        <Column spacing={1} sx={{ alignItems: "center" }}>
                            <Text variant="h4">Processing Swap</Text>
                            <Text variant="body2" color="secondary" sx={{ textAlign: "center" }}>
                                {getStatusMessage(status)}
                            </Text>
                            <Text variant="caption" color="secondary" sx={{ mt: 1 }}>
                                This typically takes 3-5 minutes
                            </Text>
                        </Column>
                    </>
                )}

                {needsClaim && (
                    <>
                        {claimQuoteIsLoading ? (
                            <CircularProgress size={48} />
                        ) : (
                            <CheckCircle sx={{ fontSize: 48, color: "success.main" }} />
                        )}
                        <Column spacing={1} sx={{ alignItems: "center" }}>
                            <Text variant="h4">Ready to Claim</Text>
                            <Text variant="body2" color="secondary" sx={{ textAlign: "center" }}>
                                {claimQuoteIsLoading
                                    ? "Preparing your claim transaction…"
                                    : `Sign to receive your ${buyToken?.symbol}.`}
                            </Text>
                        </Column>
                    </>
                )}

                {isComplete && !needsClaim && (
                    <>
                        <CheckCircle sx={{ fontSize: 48, color: "success.main" }} />
                        <Column spacing={1} sx={{ alignItems: "center" }}>
                            <Text variant="h4">{completion.title}</Text>
                            <Text variant="body2" color="secondary">
                                {buyToken?.symbol} has been delivered to your wallet
                            </Text>
                            {completion.kind === "intermediate" && (
                                <Text variant="body2" color="secondary" sx={{ textAlign: "center" }}>
                                    {completion.description}
                                </Text>
                            )}
                            {settlementTxHash && (
                                <Text
                                    variant="caption"
                                    color="primary"
                                    sx={{ cursor: "pointer", textDecoration: "underline" }}
                                    onClick={() =>
                                        window.open(explorerTxUrl(settlementTxHash), "_blank", "noopener,noreferrer")
                                    }
                                >
                                    View Transaction
                                </Text>
                            )}
                        </Column>
                    </>
                )}

                {isFailed && (
                    <>
                        <ErrorIcon sx={{ fontSize: 48, color: "error.main" }} />
                        <Column spacing={1} sx={{ alignItems: "center" }}>
                            <Text variant="h4">Swap Failed</Text>
                            <Text variant="body2" color="secondary" sx={{ textAlign: "center" }}>
                                {error || "An error occurred during settlement"}
                            </Text>
                            {status === AsyncSwapStatus.Reversed && (
                                <Text variant="body2" color="secondary">
                                    Your tokens have been returned to your wallet
                                </Text>
                            )}
                        </Column>
                    </>
                )}
            </Column>

            {needsClaim && claimQuoteError && (
                <TagTextAlert color="warning" icon="warning">
                    {getPandoraErrorMessage(claimQuoteError, "Unable to prepare claim transaction.")}
                </TagTextAlert>
            )}

            {needsClaim && (
                <Column spacing={1}>
                    <AppButton
                        fullWidth
                        size="large"
                        asyncCta={{ onClickWithResult: executeClaim }}
                        isTransaction
                        disabled={!claimQuote || claimQuoteIsLoading || !!claimQuoteError}
                    >
                        Claim {buyToken?.symbol}
                    </AppButton>
                </Column>
            )}

            {isComplete && !needsClaim && (
                <Column spacing={1}>
                    {completion.kind === "intermediate" && (
                        <AppButton fullWidth size="large" onClick={completion.onContinue} isTransaction={false}>
                            {completion.continueLabel}
                        </AppButton>
                    )}
                    <AppButton
                        fullWidth
                        size="large"
                        color={completion.kind === "intermediate" ? "secondary" : undefined}
                        onClick={onClose}
                        isTransaction={false}
                    >
                        Close
                    </AppButton>
                </Column>
            )}

            {isFailed && (
                <Column spacing={1}>
                    <AppButton fullWidth size="large" onClick={onClose} isTransaction={false}>
                        Close
                    </AppButton>
                </Column>
            )}

            {isPolling && (
                <TagTextAlert color="info" icon="tooltip">
                    You can safely close this window. Your {buyToken?.symbol} will arrive in your wallet when settlement
                    completes.
                </TagTextAlert>
            )}
        </Column>
    );
}

function useExecuteClaim(
    claimQuote: SwapInstructionsResponse | undefined,
    userWallet: string | undefined,
    onClose?: () => void
) {
    const send = useSignAndSendTransactionSender();
    const swapTransactionDetails = useTokenSwapTransaction();

    return useCallback(async (): Promise<Result<TransactionResult[]>> => {
        if (!claimQuote || !userWallet) {
            return Result.errFromMessage(MISSING_PARAM_ERROR);
        }
        return send(
            swapTransactionDetails,
            { swapQuote: claimQuote, userWallet },
            {
                messageOverrides: {
                    onSuccess: "Withdrawal complete",
                    onError: "Claim failed to confirm"
                },
                sendOptions: { onSuccess: () => onClose?.() },
                preventAutoDialogClose: true
            }
        );
    }, [claimQuote, userWallet, onClose, send, swapTransactionDetails]);
}

function getStatusMessage(status: AsyncSwapStatus | "idle"): string {
    switch (status) {
        case AsyncSwapStatus.Awaiting:
            return "Waiting for transaction confirmation";
        case AsyncSwapStatus.Processing:
            return "Planning settlement execution";
        case AsyncSwapStatus.Provisioning:
            return "Acquiring settlement capital";
        case AsyncSwapStatus.Converting:
            return "Converting to target chain";
        case AsyncSwapStatus.Settling:
            return "Delivering tokens to your wallet";
        default:
            return "";
    }
}
